/* eslint-disable quote-props */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import {
    Link,
    Stack,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    Checkbox,
    Alert,
    Divider,
    Button,
    Tooltip
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from 'components/iconify';
import { MessageQuestionCustomLinearIcon } from 'icons-lib';
import CheckboxCustom from 'components/checkbox';
// server
import { useStateSelector } from 'context/StateSelectors';
// i18n
import i18n from 'i18';
// utils
import { formatError, hasError } from 'utils/formatError';

// ----------------------------------------------------------------------

export default function RegistrationForm({ handleChangeStep, code, email }) {
    const server = useStateSelector((state) => state.server);
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const [buttonClicked, setButtonClicked] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [loading, setLoading] = useState(false);

    const [organization, setOrganization] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [error, setError] = useState('');

    const handleClick = () => {
        setError('');
        setButtonClicked(true);

        let data = {
            firstName,
            lastName,
            password,
            email,
            organization,
            code
        };

        if (!firstName || !lastName || !password) {
            setError(i18n.t('errors.fillAllRequiredFields'));
            return;
        }

        if (password !== confirmPassword) {
            setError(i18n.t('errors.passwordAndConfirmationDoNotMatch'));
            return;
        }

        if (!accepted) {
            setError(i18n.t('errors.acceptPrivacyAndTerms'));
            return;
        }

        setLoading(true);

        server.auth.register(data).then((result) => {
            if (!hasError(result)) {
                setLoading(false);
                navigate('/app/dashboard', { replace: true });
            } else {
                setLoading(false);
                setError(formatError(result));
            }
        });
    };

    return (
        <>
            <Stack spacing={2} sx={{ mt: 2 }}>
                <Stack spacing={1}>
                    <Typography variant="body2" color="textSecondary">
                        {i18n.t('columns.firstName')}
                    </Typography>
                    <TextField
                        name="firstName"
                        placeholder={i18n.t('columns.firstName')}
                        value={firstName}
                        required
                        error={buttonClicked && firstName === ''}
                        onChange={(event) => setFirstName(event.target.value)}
                    />
                </Stack>

                <Stack spacing={1}>
                    <Typography variant="body2" color="textSecondary">
                        {i18n.t('columns.lastName')}
                    </Typography>
                    <TextField
                        name="lastName"
                        placeholder={i18n.t('columns.lastName')}
                        value={lastName}
                        required
                        error={buttonClicked && lastName === ''}
                        onChange={(event) => setLastName(event.target.value)}
                    />
                </Stack>

                <Stack spacing={1}>
                    <Typography variant="body2" color="textSecondary">
                        {i18n.t('columns.organization')}
                    </Typography>
                    <TextField
                        name="organization"
                        placeholder={i18n.t('placeholder.organization')}
                        value={organization}
                        onChange={(event) =>
                            setOrganization(event.target.value)
                        }
                    />
                </Stack>

                <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" color="textSecondary">
                            {i18n.t('columns.password')}
                        </Typography>
                        <Tooltip title={i18n.t('auth.passwordTooltip')}>
                            <IconButton size="small" sx={{ p: 0 }}>
                                <MessageQuestionCustomLinearIcon
                                    sx={{
                                        fontSize: 16,
                                        color: 'text.secondary'
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <TextField
                        name="password"
                        placeholder={i18n.t('auth.enterPassword')}
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        required
                        error={
                            buttonClicked &&
                            (password === '' || password !== confirmPassword)
                        }
                        onChange={(event) => setPassword(event.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() =>
                                            setShowPassword(!showPassword)
                                        }
                                        edge="end"
                                    >
                                        <Iconify
                                            icon={
                                                showPassword
                                                    ? 'eva:eye-fill'
                                                    : 'eva:eye-off-fill'
                                            }
                                        />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Stack>

                <Stack spacing={1}>
                    <Typography variant="body2" color="textSecondary">
                        {i18n.t('columns.confirmPassword')}
                    </Typography>
                    <TextField
                        name="confirmPassword"
                        placeholder={i18n.t('columns.confirmPassword')}
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        required
                        error={
                            buttonClicked &&
                            (confirmPassword === '' ||
                                password !== confirmPassword)
                        }
                        onChange={(event) =>
                            setConfirmPassword(event.target.value)
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() =>
                                            setShowPassword(!showPassword)
                                        }
                                        edge="end"
                                    >
                                        <Iconify
                                            icon={
                                                showPassword
                                                    ? 'eva:eye-fill'
                                                    : 'eva:eye-off-fill'
                                            }
                                        />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                    <CheckboxCustom
                        checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                    />
                    <Typography>
                        {i18n.t('auth.iaccept')}{' '}
                        <Link target="_blank" href="/docs/privacy">
                            {i18n.t('auth.privacyPolicy')}
                        </Link>{' '}
                        {i18n.t('auth.and')}{' '}
                        <Link target="_blank" href="/docs/tos">
                            {i18n.t('auth.termsAndConditions')}
                        </Link>
                    </Typography>
                </Stack>
            </Stack>

            <LoadingButton
                loading={loading}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                sx={{ my: 2 }}
                onClick={handleClick}
            >
                {i18n.t('auth.register')}
            </LoadingButton>

            {error && <Alert severity="error">{error}</Alert>}

            {/* <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ my: 2, cursor: 'pointer' }}
            >
                <Button variant="text" onClick={() => handleChangeStep()}>
                    {i18n.t('auth.back')}
                </Button>
            </Stack> */}
        </>
    );
}
